import { Capacitor } from "@capacitor/core";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonBackButton,
  IonButton,
  IonImg,
  IonContent,
  IonModal,
  IonSpinner,
  IonToast,
} from "@ionic/react";
import { HeaderTitle } from "parafolio-components";
import { Button_Audio_Play_White, Button_Audio_Stop, Button_Copy_White, Button_Info_White } from "@assets/images";
import DataController from "@controllers/DataController";
import { useHidingTabBar } from "@hooks/tabs/useHidingTabBar";
import { Prompt } from "react-router";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Editor } from "@tinymce/tinymce-react";
import { Editor as TinyMCEEditor } from "tinymce";
import { useWindowResized } from "@hooks/app/useWindowResized";
import { useRecoilStateLoadable, useRecoilValue } from "recoil";
import { IAccessibilitySettings, IUser } from "@interfaces/Interfaces";
import { accessibilityAtom, personalStatementSelector, userAtom } from "@state/State";
import { Keyboard, KeyboardInfo } from "@capacitor/keyboard";
import { format } from "date-fns";
import ContentContainer from "@components/common/ContentContainer";
import * as DatabaseService from "@controllers/DatabaseService";
import { Clipboard } from "@capacitor/clipboard";
import { useAuditHelp } from "@hooks/app/useAuditHelp";
import { useTextToSpeech } from "@hooks/accessibility/useTextToSpeech";
import * as StringUtils from "@utils/stringUtils";
import * as EditorUtils from "@utils/editorUtils";
import PersonalStatementErrorModal from "@components/profile/PersonalStatementErrorModal";
import RemoteDataController from "@controllers/RemoteDataController";
import { useConnectionStatus } from "@hooks/app/useConnectionStatus";

const PersonalStatement: React.FC = () => {
  useHidingTabBar();

  const { windowHeight } = useWindowResized();
  const { content } = useAuditHelp();
  const connectionStatus = useConnectionStatus();

  const { handleTTS, playing, stopTTS } = useTextToSpeech("personal-statement-tip-modal");
  const accessibilitySettings = useRecoilValue<IAccessibilitySettings | null>(accessibilityAtom);

  const helpModalContent = content.find((item) => item.Section === "Personal statement");

  const inputRef = useRef<TinyMCEEditor | null>(null);

  const [user, setUser] = useRecoilStateLoadable<IUser | null>(userAtom);
  const { personalStatement, personalStatementTimestamp } = useRecoilValue(personalStatementSelector);

  const [tipModalVisible, setTipModalVisible] = useState<boolean>(false);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [showSavedToast, setShowSavedToast] = useState<boolean>(false);
  const [errorModalVisible, setErrorModalVisible] = useState<boolean>(false);

  const [statement, setStatement] = useState<string>(personalStatement ?? "");

  const [keyboardActive, setKeyboardActive] = useState<boolean>(false);
  const [keyboardHeight, setKeyboardHeight] = useState<number>(0);

  const [isDirty, setIsDirty] = useState<boolean>(false);

  const isIOS = Capacitor.getPlatform() === "ios";

  useEffect(() => {
    if (isIOS) {
      Keyboard.addListener("keyboardWillShow", (info: KeyboardInfo) => {
        setKeyboardActive(true);
        setKeyboardHeight(info.keyboardHeight);
      });

      Keyboard.addListener("keyboardWillHide", () => {
        setKeyboardActive(false);
      });
    }

    return () => {
      if (isIOS) {
        Keyboard.removeAllListeners();
      }
    };
  }, [isIOS]);

  useEffect(() => {
    if (inputRef.current && windowHeight) {
      const newMaxHeight = windowHeight - EditorUtils.EDITOR_BOTTOM_MARGIN;

      inputRef.current.editorContainer.style.maxHeight = `${newMaxHeight}px`;
      inputRef.current.getBody().style.maxHeight = `${newMaxHeight}px`;
      inputRef.current.dispatch("ResizeEditor");
    }
  }, [windowHeight, inputRef]);

  const copyPressed = async (): Promise<void> => {
    const plaintext = DataController.formatHTMLToPlaintext(statement);

    if (await DataController.isWebAsync()) {
      const blobHTML = new Blob([statement], { type: "text/html" });
      const blobPlainText = new Blob([plaintext], { type: "text/plain" });
      const clipboardItem = new ClipboardItem({
        "text/html": blobHTML,
        "text/plain": blobPlainText,
      });

      navigator.clipboard.write([clipboardItem]);

      setShowToast(true);
    } else {
      Clipboard.write({
        string: plaintext,
      });

      setShowToast(true);
    }
  };
  const getLastEditedPersonalStatement = (): string => {
    let value = "";

    if (user.state === "hasValue" && user.contents && user.contents.personalStatementTimestamp) {
      const date = new Date(user.contents.personalStatementTimestamp);
      const dateText = format(date, "d MMM yyyy");
      const timeText = format(date, "h:mmaaa");

      value = `Last edited ${dateText} at ${timeText} · `;
    }

    return value;
  };

  const editorFocused = useCallback((): void => {
    const editor = document.getElementById("");
    editor && editor.scrollIntoView();

    try {
      Capacitor.getPlatform() !== "web" && Keyboard.show();
    } catch (error) {
      console.log(error);
    }
  }, []);

  const onTextChanged = useCallback(
    (a: string): void => {
      if (DataController.getWordCount(a) <= EditorUtils.PERSONAL_STATEMENT_MAX_WORD_COUNT) {
        setStatement(a);

        if (statement !== a) {
          setIsDirty(true);
        } else {
          setIsDirty(false);
        }
      } else {
        setStatement((prevStatement) => prevStatement.trim());
      }
    },
    [setStatement]
  );

  const savePersonalStatement = async (): Promise<void> => {
    try {
      setSaving(true);

      if (!connectionStatus?.connected) {
        setErrorModalVisible(true);
        return;
      }

      if (user.state === "hasValue" && user.contents) {
        const newStatement = statement;
        const newTimestamp = new Date().toISOString();

        await DatabaseService.updateUserDetails(user.contents, {
          personalStatement: newStatement,
          personalStatementTimestamp: newTimestamp,
        });

        setUser((prevUser) => {
          if (prevUser) {
            const updatedUser = {
              ...prevUser,
              personalStatement: newStatement,
              personalStatementTimestamp: newTimestamp,
            };

            return updatedUser;
          }

          return prevUser;
        });

        setShowSavedToast(true);
        setSaving(false);
        setIsDirty(false);
      }
    } catch (error: unknown) {
      console.error(error);
      RemoteDataController.logError(error as Error | string);
      setErrorModalVisible(true);
    } finally {
      setSaving(false);
    }
  };

  const wordCount = DataController.getWordCount(statement);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar
          mode="ios"
          class="navBar"
          style={{
            maxWidth: DataController.isWeb() ? 980 : undefined,
            height: Capacitor.getPlatform() === "android" ? "54px" : "unset",
          }}
        >
          <IonButtons slot="start">
            <IonBackButton
              className="header-back-buttons"
              defaultHref="/profile"
              text={DataController.getBackIconText()}
              icon={DataController.getBackIconType()}
              style={{
                marginLeft: Capacitor.getPlatform() === "android" ? 8 : 0,
                "--icon-font-size": Capacitor.getPlatform() === "android" ? "24px" : "30px",
              }}
            />
          </IonButtons>
          <HeaderTitle>{"Personal Statement"}</HeaderTitle>
          <IonButtons slot="end">
            <IonButton
              className="header-button"
              mode="ios"
              onClick={() => copyPressed()}
            >
              <IonImg
                alt="Copy"
                title="Copy"
                src={Button_Copy_White}
                className="!size-[30px]"
              />
            </IonButton>
            <IonButton
              className="header-button"
              mode="ios"
              onClick={() => setTipModalVisible(true)}
            >
              <IonImg
                alt="Help"
                title="Help"
                src={Button_Info_White}
                className="!size-11"
              />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <PersonalStatementErrorModal
          visible={errorModalVisible}
          dismiss={() => setErrorModalVisible(false)}
          copy={() => {
            copyPressed();
            setErrorModalVisible(false);
          }}
        />
        <IonModal
          id="summaryOfWorkModal"
          isOpen={tipModalVisible}
          backdropDismiss={true}
          canDismiss={true}
          className="fullscreen"
          onDidDismiss={() => setTipModalVisible(false)}
        >
          <IonHeader>
            <IonToolbar
              mode="ios"
              className="navBar"
              style={{
                maxWidth: DataController.isWeb() ? 980 : undefined,
                height: Capacitor.getPlatform() === "android" ? "54px" : "unset",
              }}
            >
              <IonButtons slot="start">
                <IonButton
                  className="cancelButton"
                  mode="ios"
                  onClick={() => {
                    stopTTS();
                    setTipModalVisible(false);
                  }}
                >
                  {"Close"}
                </IonButton>
              </IonButtons>
              <HeaderTitle>{"Help"}</HeaderTitle>
              {accessibilitySettings?.textToSpeech && (
                <IonButtons slot="end">
                  <IonButton
                    className="header-button"
                    mode="ios"
                    onClick={() => handleTTS()}
                  >
                    <IonImg
                      alt="Audio Control"
                      title={playing ? "Stop" : "Play"}
                      src={playing ? Button_Audio_Stop : Button_Audio_Play_White}
                      className="headerIcon"
                    />
                  </IonButton>
                </IonButtons>
              )}
            </IonToolbar>
          </IonHeader>
          <IonContent className="page-background whiteBackground">
            <ContentContainer>
              <div
                className="infoModalContainer"
                id="personal-statement-tip-modal"
              >
                <div className="tipModalTitle text-start">{"Tips for writing"}</div>
                <div
                  className="tipModalText"
                  dangerouslySetInnerHTML={StringUtils.sanitizeHTML(helpModalContent?.HTML ?? "")}
                />
              </div>
            </ContentContainer>
          </IonContent>
        </IonModal>
        <ContentContainer
          style={{
            paddingBottom: keyboardActive ? keyboardHeight : "24px",
          }}
        >
          <div className="summaryContainer">
            <div className="wordCount">
              {personalStatementTimestamp && <span>{getLastEditedPersonalStatement()}</span>}
              <span>
                <span>{`${wordCount} word${wordCount === 1 ? "" : "s"} (max ${EditorUtils.PERSONAL_STATEMENT_MAX_WORD_COUNT})`}</span>
              </span>
            </div>
            <div>
              <Editor
                tinymceScriptSrc={process.env.PUBLIC_URL + "/tinymce/tinymce.min.js"}
                id="summaryOfWorkEditorRef"
                onInit={(event, editor) => (inputRef.current = editor)}
                value={statement}
                onEditorChange={(a, editor) => {
                  a.length > 0 && editor.getBody().setAttribute("data-mce-placeholder", "");
                  onTextChanged(a);
                }}
                onFocus={() => editorFocused()}
                init={{
                  // height: 500,
                  placeholder: "Add your personal statement here...",
                  browser_spellcheck: true,
                  branding: false,
                  menubar: false,
                  paste_data_images: false,
                  content_style: EditorUtils.editorContentStyle,
                  plugins: ["autolink", "autoresize", "wordcount", "lists"],
                  invalid_styles: {
                    "*": "color line-height mso-outline-level background font-family",
                  },
                  style_formats: [
                    {
                      title: "Headings",
                      items: [
                        { title: "Heading 1", block: "h1" },
                        { title: "Heading 2", block: "h2" },
                        { title: "Heading 3", block: "h3" },
                      ],
                    },
                    {
                      title: "Inline",
                      items: [
                        { title: "Bold", block: "strong" },
                        { title: "Italic", block: "em" },
                        { title: "Underline", block: "u" },
                        { title: "Strikethrough", block: "s" },
                      ],
                    },
                    {
                      title: "Blocks",
                      items: [{ title: "Paragraph", block: "p" }],
                    },
                  ],
                  fontsize_formats: "8pt 10pt 12pt 14pt 18pt 24pt 36pt",
                  toolbar: [
                    "undo redo | styles fontsize | bold italic underline | bullist numlist | alignleft aligncenter alignright",
                  ],
                  mobile: {
                    toolbar_mode: "sliding",
                  },
                  autoresize_bottom_margin: EditorUtils.EDITOR_AUTORESIZE_BOTTOM_MARGIN,
                  max_height: window.innerHeight - EditorUtils.EDITOR_BOTTOM_MARGIN,
                  // toolbar: ["undo redo | bold italic underline | bullist numlist"],
                }}
                licenseKey="gpl"
              />
            </div>
            <IonButton
              mode="ios"
              disabled={saving}
              className="flex w-full max-w-button mb-12px self-center mt-3"
              onClick={() => savePersonalStatement()}
            >
              {saving ? (
                <IonSpinner className="h-save-spinner w-save-spinner text-white mt-save-spinner" />
              ) : (
                <div>{"Save"}</div>
              )}
            </IonButton>
          </div>
        </ContentContainer>
        <IonToast
          isOpen={showSavedToast}
          mode="ios"
          position="bottom"
          cssClass="personal-statement-saved-toast"
          id="personal-statement-saved-toast"
          onDidDismiss={() => setShowSavedToast(false)}
          message="Personal statement saved"
          duration={1000}
          icon=""
        />
        <IonToast
          isOpen={showToast}
          mode="ios"
          position="bottom"
          cssClass="copy-to-clipboard-toast"
          id="copy-to-clipboard-toast"
          onDidDismiss={() => setShowToast(false)}
          message="Copied to clipboard"
          duration={1500}
        />
        <Prompt
          when={isDirty}
          message="You have unsaved changes, are you sure you want to leave?"
        />
      </IonContent>
    </IonPage>
  );
};

export default PersonalStatement;
